/**
 * Human-friendly display names and descriptions for agent tools.
 *
 * SSOT for tool presentation across the UI — used by ChatPanel's
 * live tool call traces and any future tool-related UI.
 *
 * SOLID:
 *   SRP — this file only maps tool IDs to display data.
 *   OCP — add a new tool by appending to TOOL_DISPLAY. No component changes.
 *   DIP — components depend on the ToolDisplayInfo interface, not on
 *         tool names or specific formatting logic.
 *
 * To add a new tool: add an entry to TOOL_DISPLAY below. The UI picks
 * it up automatically. Unknown tools fall back to the raw tool name.
 */

export interface ToolDisplayInfo {
  /** Short human label (e.g. "Reading section") */
  label: string;
  /** Verb phrase for the active/in-progress state (e.g. "Reading") */
  activeVerb: string;
  /** Verb phrase for the completed state (e.g. "Read") */
  completedVerb: string;
  /** Which parameter to show as context (e.g. "section_number") */
  contextParam?: string;
  /** Optional second parameter for richer context */
  contextParam2?: string;
}

/**
 * Tool display registry. Keyed by the tool name as sent by the agent.
 */
const TOOL_DISPLAY: Record<string, ToolDisplayInfo> = {
  // Document tools
  doc_list_sections: {
    label: "Document structure",
    activeVerb: "Scanning",
    completedVerb: "Scanned",
  },
  doc_read_section: {
    label: "Section content",
    activeVerb: "Reading",
    completedVerb: "Read",
    contextParam: "section_number",
  },
  doc_write_section: {
    label: "Section update",
    activeVerb: "Writing",
    completedVerb: "Updated",
    contextParam: "section_number",
  },
  doc_get_provenance: {
    label: "Generation provenance",
    activeVerb: "Checking provenance of",
    completedVerb: "Checked provenance of",
    contextParam: "section_number",
  },
  doc_diff: {
    label: "Change detection",
    activeVerb: "Comparing",
    completedVerb: "Compared",
    contextParam: "section_number",
  },
  doc_search: {
    label: "Document search",
    activeVerb: "Searching for",
    completedVerb: "Found",
    contextParam: "query",
  },

  // Retrieval tools (Asthra)
  find_section: {
    label: "Source lookup",
    activeVerb: "Looking up",
    completedVerb: "Found",
    contextParam: "section_label",
    contextParam2: "file_label",
  },
  find_table: {
    label: "Table lookup",
    activeVerb: "Looking up",
    completedVerb: "Found",
    contextParam: "table_label",
    contextParam2: "file_label",
  },
  search_library: {
    label: "Library search",
    activeVerb: "Searching",
    completedVerb: "Searched",
    contextParam: "query",
  },
  get_document_inventory: {
    label: "Document inventory",
    activeVerb: "Loading contents of",
    completedVerb: "Loaded",
    contextParam: "file_label",
  },
  find_image: {
    label: "Image lookup",
    activeVerb: "Finding image in",
    completedVerb: "Found image in",
    contextParam: "file_label",
  },

  // Literature tools
  search_literature: {
    label: "Literature search",
    activeVerb: "Searching PubMed for",
    completedVerb: "Found results for",
    contextParam: "query",
  },

  // Provenance tool
  query_provenance: {
    label: "Run provenance",
    activeVerb: "Querying",
    completedVerb: "Retrieved",
    contextParam: "kind",
  },

  // User input
  request_user_input: {
    label: "Waiting for input",
    activeVerb: "Asking",
    completedVerb: "Received input",
    contextParam: "question",
  },

  // Abbreviations table (/abbreviations command — client-driven stream)
  scan_sections: {
    label: "the draft",
    activeVerb: "Reading",
    completedVerb: "Read",
  },
  extract_abbreviations: {
    label: "abbreviations",
    activeVerb: "Extracting",
    completedVerb: "Extracted",
  },
  infer_abbreviations: {
    label: "undefined acronyms",
    activeVerb: "Resolving",
    completedVerb: "Resolved",
  },

  // Draft submission
  submit_draft: {
    label: "Draft submission",
    activeVerb: "Submitting",
    completedVerb: "Submitted",
    contextParam: "title",
  },
  submit_rewrite: {
    label: "Rewrite submission",
    activeVerb: "Submitting rewrite",
    completedVerb: "Submitted rewrite",
  },

  // AsthraAgent toolbelt — the unified drafting / rewrite loop's tools. These
  // used to live only in the spine's private verb map; one registry now feeds
  // the spine subline, the live activity feed, the chat rail and Action History.
  retrieve_from_library: {
    label: "Library retrieval",
    activeVerb: "Retrieving",
    completedVerb: "Retrieved",
    contextParam: "query",
  },
  view_document_pages: {
    label: "Page images",
    activeVerb: "Viewing pages of",
    completedVerb: "Viewed pages of",
    contextParam: "file_label",
  },
  create_table_artefact: {
    label: "Table artefact",
    activeVerb: "Building table",
    completedVerb: "Built table",
    contextParam: "query",
  },
  search_across_documents: {
    label: "Cross-document search",
    activeVerb: "Searching",
    completedVerb: "Searched",
    contextParam: "query",
  },
  get_document_tree: {
    label: "Document tree",
    activeVerb: "Scanning",
    completedVerb: "Scanned",
    contextParam: "file_label",
  },
  get_document_metadata: {
    label: "Document metadata",
    activeVerb: "Scanning",
    completedVerb: "Scanned",
    contextParam: "file_label",
  },
  get_asset_inventory: {
    label: "Asset inventory",
    activeVerb: "Indexing",
    completedVerb: "Indexed",
    contextParam: "file_label",
  },
  retrieve_module_instructions: {
    label: "Section instructions",
    activeVerb: "Loading instructions for",
    completedVerb: "Loaded instructions for",
    contextParam: "module_id",
  },
  get_prior_section: {
    label: "Prior section",
    activeVerb: "Reading prior section",
    completedVerb: "Read prior section",
    contextParam: "section_number",
  },
  semantic_match: {
    label: "Semantic match",
    activeVerb: "Reading",
    completedVerb: "Read",
    contextParam: "query",
  },
  retrieve_and_extract: {
    label: "Extraction",
    activeVerb: "Reading",
    completedVerb: "Read",
    contextParam: "query",
  },
  dynamic_retrieval: {
    label: "Retrieval",
    activeVerb: "Reading",
    completedVerb: "Read",
    contextParam: "query",
  },
  generate_text: {
    label: "Drafting",
    activeVerb: "Drafting",
    completedVerb: "Drafted",
  },
};

/**
 * Get display info for a tool. Falls back to a generated display
 * from the raw tool name (e.g. "doc_read_section" → "Doc read section").
 */
export function getToolDisplay(toolName: string): ToolDisplayInfo {
  if (TOOL_DISPLAY[toolName]) {
    return TOOL_DISPLAY[toolName];
  }
  // Fallback: convert snake_case to readable label
  const readable = toolName.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  return {
    label: readable,
    activeVerb: "Running",
    completedVerb: "Completed",
  };
}

/**
 * Build a human-friendly description of a tool call.
 *
 * @param toolName - The raw tool name
 * @param input - The tool's input parameters
 * @param isComplete - Whether the call has finished
 * @returns A string like "Reading section 4.0" or "Searched library for 'OCT imaging'"
 */
export function describeToolCall(toolName: string, input: Record<string, unknown>, isComplete: boolean): string {
  const info = getToolDisplay(toolName);
  const verb = isComplete ? info.completedVerb : info.activeVerb;

  // Build context from parameters
  const parts: string[] = [verb];
  if (info.contextParam && input[info.contextParam]) {
    const val = String(input[info.contextParam]).slice(0, 50);
    if (info.contextParam2 && input[info.contextParam2]) {
      parts.push(`"${val}" in ${String(input[info.contextParam2]).slice(0, 40)}`);
    } else {
      parts.push(info.contextParam === "query" ? `"${val}"` : `§${val}`);
    }
  } else {
    parts.push(info.label.toLowerCase());
  }

  return parts.join(" ");
}

/** Lowercase the first character only — keeps inner casing like
 *  "PubMed" intact when a verb phrase is dropped after "Asthra is ". */
function lowerFirst(s: string): string {
  return s.length > 0 ? s.charAt(0).toLowerCase() + s.slice(1) : s;
}

/**
 * Build the present-tense phrase shown in a live "Asthra is …" status
 * line, e.g. `searching PubMed for "OCT imaging"` or `reading §9.2`.
 *
 * Reuses the same SSOT verbs/context as {@link describeToolCall} so the
 * streaming status line and the completed tool-call trace never drift
 * apart. The returned phrase is lowercased at the first character so it
 * reads naturally after "Asthra is ".
 *
 * @param toolName Raw tool name from the SSE stream.
 * @param input Tool input parameters (carries the context param).
 * @param surface Optional retrieval surface ("online", "doc", …). Adds a
 *   destination qualifier ("searching online") only when the verb is a
 *   single bare word that doesn't already name one.
 */
export function buildLiveStatusPhrase(toolName: string, input?: Record<string, unknown>, surface?: string): string {
  const info = getToolDisplay(toolName);
  const ctxVal = info.contextParam && input ? input[info.contextParam] : undefined;
  const hasContext = ctxVal != null && String(ctxVal).trim() !== "";
  if (hasContext) {
    return lowerFirst(describeToolCall(toolName, input ?? {}, false));
  }
  const verb = lowerFirst(info.activeVerb);
  if (surface === "online" && !verb.includes(" ")) {
    return `${verb} online`;
  }
  return verb;
}

/**
 * Fit a free-form query on one line: strip newlines, collapse whitespace,
 * ellipsise over the limit.
 */
export function truncateQuery(s: string, limit = 60): string {
  const cleaned = s.replace(/\s+/g, " ").trim();
  return cleaned.length > limit ? `${cleaned.slice(0, limit - 1)}…` : cleaned;
}

/**
 * Inject a pre-flattened query string under the tool's context parameter.
 *
 * The run SSE stream carries ``tool_query`` as one string (``asthra_agent._tool_query``
 * flattens the input), not the input dict the chat rail gets — so the spine
 * cannot call {@link describeToolCall} directly. Re-keying the string under the
 * tool's ``contextParam`` lets both surfaces share the same verbs.
 */
function inputFromQuery(toolName: string, query?: string | null): Record<string, unknown> | undefined {
  if (query == null || String(query).trim() === "") return undefined;
  const info = getToolDisplay(toolName);
  return { [info.contextParam ?? "query"]: truncateQuery(String(query)) };
}

/** Live phrase for a ``tool_call_start`` off the run stream, e.g. `looking up §9.2`. */
export function buildLiveStatusPhraseFromQuery(toolName: string, query?: string | null, surface?: string): string {
  return buildLiveStatusPhrase(toolName, inputFromQuery(toolName, query), surface);
}

/** Completed phrase for a ``tool_call_result`` off the run stream, e.g. `Found §9.2`. */
export function describeToolCallFromQuery(toolName: string, query: string | null | undefined, isComplete: boolean): string {
  return describeToolCall(toolName, inputFromQuery(toolName, query) ?? {}, isComplete);
}
